import React, { FC, useEffect, useRef } from 'react'
import { Box, Typography, keyframes, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { CleoMessage } from '../../../../models/cleo.model'
import MessageBubble from './MessageBubble'

type MessageListProps = {
    messages: CleoMessage[]
    onAction: (text: string) => void
}

const fadeInUp = keyframes`
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
`

const GREETING =
    'Olá! Sou a Cléo, sua assistente de formulação. Posso sugerir ativos, conferir concentrações e incompatibilidades e montar fórmulas. Como posso ajudar hoje?'

const MessageList: FC<MessageListProps> = ({ messages, onAction }) => {
    const theme = useTheme()
    const bottomRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [messages])

    if (messages.length === 0) {
        return (
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.25,
                        animation: `${fadeInUp} 0.4s ease`,
                    }}
                >
                    <Box
                        sx={{
                            flexShrink: 0,
                            width: 34,
                            height: 34,
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'primary.main',
                            color: theme.palette.primary.contrastText,
                        }}
                    >
                        <AutoAwesomeIcon sx={{ fontSize: 17 }} />
                    </Box>
                    <Box
                        sx={{
                            maxWidth: 560,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: '4px 16px 16px 16px',
                            px: 2,
                            py: 1.5,
                            boxShadow: `0 1px 4px ${alpha(theme.palette.common.black, 0.04)}`,
                        }}
                    >
                        <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.primary' }}>
                            {GREETING}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        )
    }

    return (
        <Box
            sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-thumb': {
                    bgcolor: 'action.disabled',
                    borderRadius: 3,
                },
            }}
        >
            {messages.map((msg, idx) => (
                <MessageBubble key={idx} message={msg} onAction={onAction} />
            ))}
            <div ref={bottomRef} />
        </Box>
    )
}

export default MessageList